import React, { useState, useEffect } from 'react';
import { Search, Filter, Grid, List, Star, ShoppingCart, Eye } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { useCart } from '../context/CartContext';
import { translateProductName } from '../utils/translateProductName';
import { Product } from '../services/productService';

const Products = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('featured');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const { addToCart, isUpdating } = useCart();

  // Read category from URL query params
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, [searchParams]);

  const handleAddToCart = async (productId: number) => {
    try {
      await addToCart(productId.toString(), 1);
      console.log("✅ Produto adicionado ao carrinho:", productId);
    } catch (error) {
      console.error("❌ Erro ao adicionar produto:", error);
    }
  };

  // Carregar produtos do Supabase
  useEffect(() => {
    const fetchProducts = async () => {
      console.log('🔍 [Products] Iniciando busca de produtos...');
      try {
        const { data, error } = await supabase
          .from('products')
          .select('*')
          .order('id');

        if (error) {
          console.error('❌ [Products] Erro Supabase:', error.message);
          setProducts([]);
        } else if (data && data.length > 0) {
          console.log(`✅ [Products] ${data.length} produtos carregados:`, data.map(p => p.name));
          setProducts(data);
        } else {
          console.log("📦 [Products] Nenhum produto retornado do banco");
          setProducts([]);
        }
      } catch (err) {
        console.error('❌ [Products] Exception:', err);
        setProducts([]);
      } finally {
        console.log('🏁 [Products] setLoading(false)');
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Categorias dinâmicas baseadas nos produtos - CORRIGIDO
  const categories = React.useMemo(() => {
    const productCategories = [...new Set(products.map(p => p.category))];
    
    // Função para mapear nomes de categorias
    const getCategoryName = (cat: string) => {
      const categoryMap: Record<string, string> = {
        'detox': t('categories.detoxTitle'),
        'Detox Natural': t('categories.detoxTitle'),
        'emagrecimento': t('categories.weightLossTitle'),
        'weightLoss': t('categories.weightLossTitle'),
        'Queima Gordura': t('categories.weightLossTitle'),
        'Gewichtsverlust': t('categories.weightLossTitle'),
        'fibra': t('categories.fiberTitle'),
        'fiber': t('categories.fiberTitle'),
        'Fibra Digestiva': t('categories.fiberTitle'),
        'Ballaststoffe': t('categories.fiberTitle'),
        'packs': t('categories.packsTitle'),
        'Pacotes': t('categories.packsTitle'),
        'energia': t('categories.energyTitle'),
        'energy': t('categories.energyTitle'),
        'Energia': t('categories.energyTitle')
      };
      
      return categoryMap[cat] || cat.charAt(0).toUpperCase() + cat.slice(1);
    };
    
    return [
      { id: 'all', name: t('filters.allCategories'), count: products.length },
      ...productCategories.map(cat => ({
        id: cat,
        name: getCategoryName(cat),
        count: products.filter(p => p.category === cat).length
      }))
    ];
  }, [products, t]);

  // Filtrar e ordenar produtos
  useEffect(() => {
    let filtered = [...products];

    // Filtrar por categoria
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => product.category?.toLowerCase() === selectedCategory.toLowerCase());
    }

    // Filtrar por busca
    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Ordenar
    switch (sortBy) {
      case 'priceLowHigh':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'priceHighLow':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'bestRating':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      case 'nameAZ':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default: // featured
        filtered.sort((a, b) => (b.badge ? 1 : 0) - (a.badge ? 1 : 0));
    }

    console.log(`🔄 [Products Filter] products=${products.length}, filtered=${filtered.length}, category=${selectedCategory}, search=${searchTerm}`);
    setFilteredProducts(filtered);
  }, [products, selectedCategory, searchTerm, sortBy]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-48 mb-8"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg shadow p-4">
                  <div className="h-48 bg-gray-300 rounded mb-4"></div>
                  <div className="h-4 bg-gray-300 rounded mb-2"></div>
                  <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-bold text-gray-900">
            {t('nav.products')}
          </h1>
          <p className="text-gray-600 mt-2">
            {t('productsPage.subtitle')}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar - Filtros */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-lg shadow p-6 sticky top-24">
              {/* Busca */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('filters.search')}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('filters.searchPlaceholder')}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4ECDC4] focus:border-transparent"
                  />
                  <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                </div>
              </div>

              {/* Categorias */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('filters.categories')}
                </label>
                <div className="space-y-2">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                        selectedCategory === category.id
                          ? 'bg-[#4ECDC4] text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {category.name} ({category.count})
                    </button>
                  ))}
                </div>
              </div>

              {/* Ordenação */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('filters.sort')}
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-[#4ECDC4] focus:border-transparent"
                >
                  <option value="featured">{t('filters.featured')}</option>
                  <option value="priceLowHigh">{t('filters.priceLowHigh')}</option>
                  <option value="priceHighLow">{t('filters.priceHighLow')}</option>
                  <option value="bestRating">{t('filters.bestRating')}</option>
                  <option value="nameAZ">{t('filters.nameAZ')}</option>
                </select>
              </div>
            </div>
          </div>

          {/* Conteúdo Principal */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-gray-600">
                {t('productsPage.productsFound', { count: filteredProducts.length })}
              </p>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md ${
                    viewMode === 'grid' ? 'bg-[#4ECDC4] text-white' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <Grid className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md ${
                    viewMode === 'list' ? 'bg-[#4ECDC4] text-white' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <List className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Grid de Produtos */}
            <div className={`grid gap-6 ${
              viewMode === 'grid'
                ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                : 'grid-cols-1'
            }`}>
              {filteredProducts.map((product) => (
                <div
                  key={product.id}
                  className={`bg-white rounded-lg shadow hover:shadow-lg transition-shadow ${
                    viewMode === 'list' ? 'flex' : ''
                  }`}
                >
                  {/* Imagem */}
                  <div className={`relative ${viewMode === 'list' ? 'w-48 h-48' : 'h-64'} overflow-hidden rounded-t-lg ${viewMode === 'list' ? 'rounded-l-lg rounded-tr-none' : ''}`}>
                    <Link to={`/produto/${product.id}`}>
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                      />
                    </Link>
                    {product.count_in_stock === 0 && ( 
                      <div style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0,0,0,0.7)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 10
                      }}>
                        <div style={{
                          backgroundColor: "#dc2626",
                          color: "white",
                          padding: "12px 24px",
                          borderRadius: "8px",
                          fontWeight: "bold",
                          fontSize: "18px",
                          transform: "rotate(12deg)",
                          border: "2px solid white"
                        }}>
                          {t("products.outOfStock")}
                        </div>
                      </div>
                    )}
                    {product.badge && (
                      <div className={`absolute top-2 left-2 ${product.badge_color || 'bg-red-500'} text-white px-2 py-1 rounded text-xs font-bold`}>
                        {product.badge}
                      </div>
                    )}
                    {product.original_price && (
                      <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded text-xs font-bold">
                        -{Math.round(((product.original_price - product.price) / product.original_price) * 100)}%
                      </div>
                    )}
                  </div>

                  {/* Conteúdo */}
                  <div className="p-4 flex-1">
                    <Link to={`/produto/${product.id}`}>
                      <h3 className="font-bold text-lg text-gray-900 mb-2 hover:text-[#4ECDC4] transition-colors cursor-pointer">
                        {translateProductName(product.name)}
                      </h3>
                    </Link>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                      {product.description}
                    </p>

                    {/* Rating */}
                    <div className="flex items-center mb-3">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-4 h-4 ${
                              i < Math.floor(product.rating)
                                ? 'text-yellow-400 fill-current'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-sm text-gray-600 ml-2">
                        {product.rating} ({product.num_reviews} {t('products.reviews')})
                      </span>
                    </div>

                    {/* Preço */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl font-bold text-[#4ECDC4]">
                          CHF {product.price}
                        </span>
                        {product.original_price && (
                          <span className="text-sm text-gray-500 line-through">
                            CHF {product.original_price}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      {/* Ver Mais Button */}
                      <button 
                        onClick={() => navigate(`/producto/${product.id}`)}
                        className="flex-1 bg-white border-2 border-[#4ECDC4] text-[#4ECDC4] py-2 rounded-lg font-bold hover:bg-[#4ECDC4] hover:text-white transition-all duration-200 flex items-center justify-center"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        {t('products.viewMore')}
                      </button>

                      {/* Add to Cart Button */}
                      <button 
                        onClick={() => handleAddToCart(product.id)}
                        disabled={isUpdating} 
                        className="flex-1 bg-gradient-to-r from-[#4ECDC4] to-[#95C11F] text-white py-2 rounded-lg font-bold hover:shadow-lg transition-all duration-200 flex items-center justify-center disabled:opacity-50"
                      >
                        <ShoppingCart className="w-4 h-4 mr-2" />
                        {isUpdating ? t('products.adding') : t('products.addToCart')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Empty State */}
            {filteredProducts.length === 0 && (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <Search className="w-16 h-16 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {t('productsPage.noProducts')}
                </h3>
                <p className="text-gray-600">
                  {t('productsPage.tryDifferent')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;
